import { storeSidebarData } from "../../redux/slices/sidebarSlice";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ExitToAppRoundedIcon from "@mui/icons-material/ExitToAppRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import Avatar from "@mui/material/Avatar";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import * as React from "react";

const MOBILE_SIDEBAR_EVENT = "cp-mobile-sidebar-toggle";

const sectionLabels = [
  { match: "/homepage", label: "Dashboard" },
  { match: "/reportslanding", label: "Reports" },
  { match: "/trackinglanding", label: "Tracking" },
  { match: "/trackings", label: "Tracking" },
  { match: "/trackingcenter", label: "Tracking" },
  { match: "/shipmenttrack", label: "Shipments" },
  { match: "/formslanding", label: "Forms" },
  { match: "/forms", label: "Forms" },
  { match: "/remits", label: "Remits" },
  { match: "/admin", label: "Administration" },
  { match: "/adminaccountinformation", label: "Administration" },
  { match: "/companyinformation", label: "Administration" },
  { match: "/enhancement", label: "Enhancements" },
  { match: "/unauthorized", label: "Access" },
  { match: "/downloadcenter", label: "Download Center" },
];

const getSectionLabel = (pathname) => {
  const matchingSection = sectionLabels.find(({ match }) => pathname.startsWith(match));
  return matchingSection ? matchingSection.label : "Workspace";
};

const NavBar = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const firstName = localStorage.getItem("name");
  const sectionLabel = getSectionLabel(location.pathname.toLowerCase());

  const logout = async () => {
    sessionStorage.clear();
    const cookieNames = ["gtms-cp-auth", "cp-a", "cp-csn", "cp-cn", "cp-admin", "cp-ci", "cp-un", "cp-role", "cp-vr"];
    cookieNames.forEach((cookieName) => {
      Cookies.remove(cookieName);
    });
    dispatch(storeSidebarData([]));
    navigate("/");
  };

  const navigateEnhancement = async () => {
    navigate("/Enhancement");
  };

  const openMobileSidebar = () => {
    window.dispatchEvent(new CustomEvent(MOBILE_SIDEBAR_EVENT));
  };

  return (
    <div className="navbar">
      <Box sx={{ flexGrow: 1 }}>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            color: "#ffffff",
            borderBottom: `1px solid ${alpha("#d7ecff", 0.16)}`,
            background: "linear-gradient(90deg, #081a2d 0%, #0f2a46 42%, #2e6c97 100%)",
            boxShadow: "0 18px 44px rgba(8, 26, 45, 0.24)",
            backdropFilter: "blur(12px)",
          }}
        >
          <Toolbar
            sx={{
              minHeight: 68,
              px: { xs: 1.5, sm: 2.5 },
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
              {isMobile && (
                <IconButton
                  onClick={openMobileSidebar}
                  sx={{
                    color: "#ffffff",
                    border: `1px solid ${alpha("#ffffff", 0.14)}`,
                    backgroundColor: alpha("#ffffff", 0.06),
                    "&:hover": {
                      backgroundColor: alpha("#ffffff", 0.12),
                    },
                  }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              )}
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: alpha("#ffffff", 0.12),
                  color: "#f6fbff",
                  border: `1px solid ${alpha("#ffffff", 0.18)}`,
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  boxShadow: "0 10px 24px rgba(4, 16, 31, 0.22)",
                }}
              >
                CP
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: "0.01em",
                    whiteSpace: "nowrap",
                  }}
                >
                  Customer Portal
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, mt: 0.25 }}>
                  <Typography
                    sx={{
                      fontSize: "0.74rem",
                      color: alpha("#e3f1ff", 0.76),
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {sectionLabel}
                  </Typography>
                  {firstName && (
                    <Typography
                      sx={{
                        display: { xs: "none", sm: "block" },
                        fontSize: "0.78rem",
                        color: alpha("#f4f9ff", 0.9),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {`Welcome back, ${firstName}`}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexShrink: 0 }}>
              <Chip
                label={sectionLabel}
                size="small"
                sx={{
                  display: { xs: "none", md: "inline-flex" },
                  height: 28,
                  color: "#eff8ff",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  border: `1px solid ${alpha("#ffffff", 0.16)}`,
                  backgroundColor: alpha("#ffffff", 0.08),
                  ".MuiChip-label": { px: 1.2 },
                }}
              />

              <Button
                id="enhancementPageNavBtn"
                color="inherit"
                variant="outlined"
                startIcon={<CampaignRoundedIcon />}
                onClick={navigateEnhancement}
                sx={{
                  minWidth: 0,
                  px: { xs: 1.2, sm: 1.6 },
                  py: 0.85,
                  borderRadius: 999,
                  borderColor: alpha("#d7ecff", 0.24),
                  color: "#ffffff",
                  backgroundColor: alpha("#ffffff", 0.06),
                  fontSize: "0.84rem",
                  fontWeight: 700,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    borderColor: alpha("#eff8ff", 0.36),
                    backgroundColor: alpha("#ffffff", 0.12),
                  },
                }}
              >
                {isMobile ? "Ideas" : "Feedback"}
              </Button>

              <Button
                color="inherit"
                onClick={logout}
                startIcon={<ExitToAppRoundedIcon />}
                sx={{
                  minWidth: 0,
                  px: { xs: 1.2, sm: 1.5 },
                  py: 0.85,
                  borderRadius: 999,
                  color: alpha("#f4f9ff", 0.92),
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: alpha("#ffffff", 0.1),
                    color: "#ffffff",
                  },
                }}
              >
                {isMobile ? "Exit" : "Log out"}
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>
      </Box>
    </div>
  );
};

export default NavBar;
